import { debounce } from 'lodash';
import { useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { Autocomplete, CircularProgress, TextField } from '@mui/material';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';
import { useTranslate } from 'src/locales';
import { useGetContactByIdQuery, useGetOrganizationContactsQuery } from 'src/services/contacts/contacts.service';
import type { Contact } from 'src/services/contacts/contacts.types';

type Props = {
  name?: string;               // form field, stores selected manager id (or null)
  currentContactId: string;    // excluded so a contact can't report to itself
  excludeIds?: string[];       // extra exclusions (e.g. descendants) for client-side cycle guard
  label?: string;
  placeholder?: string;
  options?: Contact[];         // supplied => company-scoped local filter; omitted => org-wide search
};

export function ReportsToField({
  name = 'reports_to_contact_id', currentContactId, excludeIds = [], label, placeholder, options,
}: Props) {
  const { t } = useTranslate();
  const { control, watch, setValue } = useFormContext();
  const authContext = useAuthContext();
  const scoped = options !== undefined;
  const [search, setSearch] = useState('');
  const currentValue = watch(name) as string | null | undefined;

  const { data: orgContacts, isFetching } = useGetOrganizationContactsQuery(
    { org_id: authContext.organization.id, page: { page: 1, per_page: 25 }, search_query: search },
    { skip: scoped || !authContext.organization.id }
  );
  const { data: currentManager } = useGetContactByIdQuery(currentValue as string, { skip: !currentValue });

  const onSearch = useMemo(() => debounce((v: string) => setSearch(v), 500), []);
  const forbidden = useMemo(() => new Set<string>([currentContactId, ...excludeIds]), [currentContactId, excludeIds]);

  const resolvedOptions = useMemo<Contact[]>(() => {
    const source = scoped ? options ?? [] : orgContacts?.results ?? [];
    const list = source.filter((c) => !forbidden.has(c.id));
    if (currentManager && !forbidden.has(currentManager.id) && !list.some((c) => c.id === currentManager.id)) {
      return [currentManager, ...list];
    }
    return list;
  }, [scoped, options, orgContacts, currentManager, forbidden]);

  const getLabel = (c: Contact) =>
    `${c.first_name} ${c.last_name ?? ''}${c.position_title ? ` — ${c.position_title}` : ''}`.trim();

  return (
    <Controller
      name={name}
      control={control}
      render={({ fieldState: { error } }) => {
        const selected = resolvedOptions.find((c) => c.id === currentValue) ?? null;
        return (
          <Autocomplete
            fullWidth
            options={resolvedOptions}
            loading={isFetching}
            value={selected}
            {...(scoped ? {} : { filterOptions: (x: Contact[]) => x, onInputChange: (_e: unknown, v: string) => onSearch(v) })}
            getOptionLabel={getLabel}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            onChange={(_e, value) =>
              setValue(name, (value as Contact | null)?.id ?? null, { shouldDirty: true, shouldValidate: true })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={label ?? t('companies.org_chart.reports_to_label')}
                placeholder={placeholder ?? t('companies.org_chart.reports_to_placeholder')}
                error={!!error}
                helperText={error?.message}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (<>{isFetching ? <CircularProgress color="inherit" size={20} /> : null}{params.InputProps.endAdornment}</>),
                }}
              />
            )}
          />
        );
      }}
    />
  );
}
